/**
 * spriteCdn (shared) — where SERVER-rendered Pokémon artwork comes from.
 *
 * The client has its own, richer copy of this at client/src/lib/spriteCdn.ts;
 * that file is the canonical explanation and the one to read first. This is the
 * slice of it that server-side code needs, living in shared/ because server/
 * cannot import from client/src.
 *
 * THE RULE
 * Artwork is served from our own R2 mirror (sprites.nexusdex.ai), never from a
 * third party. The upstream repos are a BUILD-TIME source only:
 * scripts/sync-sprites-to-r2.ts copies what we render into the bucket. Hotlinking
 * upstream at runtime makes their outage our outage, lets an upstream path
 * reorg break production with no deploy on our side, and serves our art layer
 * off someone else's CDN. scripts/ip-check.sh fails the build if an upstream
 * host reappears — including from a comment, which is why none is named here.
 *
 * WHY OFFICIAL-ARTWORK AND NOT THE BASE SPRITE TREE
 * The mirror covers `other/official-artwork` and `other/home` for every id in
 * 1..1025 (sync-sprites-to-r2.ts → urlsFromTemplatedArtwork). The base pixel
 * sprite tree is only mirrored for the ~400 ids that appear in the generated
 * forms data, so building a base-sprite URL for an arbitrary species can point
 * at a key that was never uploaded. Official artwork is both universally
 * mirrored and the right asset for a large render — the base sprites are 96px.
 */

/** Public base for our own mirrored artwork. No trailing slash. */
export const SPRITE_CDN_BASE = "https://sprites.nexusdex.ai";

/** Mirror of the upstream `sprites/pokemon/other` tree. */
const SPRITE_CDN_POKEAPI_OTHER = `${SPRITE_CDN_BASE}/pokeapi/other`;

/**
 * Full-colour official artwork for a species, from our mirror.
 *
 * Mirrors client/src/lib/pokemonArtwork.ts's officialArtworkUrl so the two
 * runtimes cannot render different art for the same Pokémon.
 */
export function officialArtworkUrl(id: number, opts?: { shiny?: boolean }): string {
  const variant = opts?.shiny ? "official-artwork/shiny" : "official-artwork";
  return `${SPRITE_CDN_POKEAPI_OTHER}/${variant}/${id}.png`;
}
